import { useState } from 'react';
import type { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChatContainer } from './components/chat';
import { DesignerContainer } from './components/designer';
import { TemplateSelector, PublishModal, PagesManager } from './components';
import { useTemplateStore } from './stores';
import './styles/index.css';

const App: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPagesManager, setShowPagesManager] = useState(false);
  
  const { getCurrentTemplate, publishedPages } = useTemplateStore();
  const currentTemplate = getCurrentTemplate();

  // 判断当前是否在工作流相关页面
  const isWorkflowPage = location.pathname.startsWith('/workflow');

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                AI可视化设计器
              </h1>
              <p className="text-xs text-gray-500">
                当前模板: {currentTemplate.name}
              </p>
            </div>
          </div>
          
          {/* Tab Switcher - 改为路由导航 */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => navigate('/')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                !isWorkflowPage
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              设计器
            </button>
            <button
              onClick={() => navigate('/workflow')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                isWorkflowPage
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              工作流
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            {/* 模板选择按钮 */}
            <button 
              onClick={() => setShowTemplateSelector(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              模板
            </button>

            {/* 页面管理按钮 */}
            {publishedPages.length > 0 && (
              <button
                onClick={() => setShowPagesManager(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                页面管理
                <span className="bg-purple-100 text-purple-600 text-xs px-1.5 py-0.5 rounded-full">
                  {publishedPages.length}
                </span>
              </button>
            )}

            {/* 发布按钮 */}
            <button 
              onClick={() => setShowPublishModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              发布
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - 设计器页面 */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full flex">
          {/* Chat Section - 35% width on desktop */}
          <div className="w-full lg:w-[35%] h-full border-r border-gray-200">
            <ChatContainer />
          </div>

          {/* Designer Section - 65% width on desktop */}
          <div className="hidden lg:block lg:flex-1 h-full">
            <DesignerContainer />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t px-6 py-2 text-center text-xs text-gray-400">
        AI可视化设计器 v0.5.0 | 阶段三-第1周 | React-Flow工作流已集成
      </footer>

      {/* 模板选择器弹窗 */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelect={(templateId) => {
          console.log('Selected template:', templateId);
        }}
      />

      {/* 发布弹窗 */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={(result) => {
          if (result.success) {
            console.log('Published:', result.url);
          } else {
            console.error('Publish failed:', result.error);
          }
        }}
      />

      {/* 页面管理弹窗 */}
      <PagesManager
        isOpen={showPagesManager}
        onClose={() => setShowPagesManager(false)}
      />
    </div>
  );
};

export default App;
